import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Avatar } from './Avatar';
import { parseFormattedText, cn } from '@/lib/utils';
import { Undo2, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ImageMessage as ImageMessageType, ImageBlock, TextBlock } from '@/types';

interface ImageMessageProps {
    message: ImageMessageType;
    botAvatar: string;
    customStyles?: {
        primaryColor?: string;
        secondaryColor?: string;
        botMessageBg?: string;
        botMessageText?: string;
        borderRadius?: number;
        fontSize?: number;
    };
    onUndo?: () => void;
}

const getGridClass = (showPerRow: number) => {
    const gridClasses: Record<number, string> = {
        1: 'image-grid-1',
        2: 'image-grid-2',
        3: 'image-grid-3',
        4: 'image-grid-4',
    };
    return gridClasses[Math.min(Math.max(showPerRow, 1), 4)] || 'image-grid-2';
};

export const ImageMessage: React.FC<ImageMessageProps> = ({ message, botAvatar, customStyles, onUndo }) => {
    const botBgColor = customStyles?.botMessageBg || customStyles?.secondaryColor || '#ffffff';
    const botTextColor = customStyles?.botMessageText ?? '#1f2937';
    const borderRadius = customStyles?.borderRadius || 16;
    const fontSize = customStyles?.fontSize ?? 14;

    return (
        <div className="zentri-flex zentri-items-start zentri-gap-3 zentri-mb-4 zentri-group zentri-animate-fade-in">
            <Avatar src={botAvatar} alt="Bot" size="md" />

            {/* Message container with undo */}
            <div className="zentri-flex zentri-items-start zentri-gap-2">
                {/* Single message bubble containing all blocks */}
                <div
                    className="zentri-px-3.5 zentri-py-2.5 zentri-max-w-lg zentri-transition-all zentri-duration-300 zentri-ease-out hover:zentri-shadow-elegant zentri-border zentri-border-neutral-100"
                    style={{
                        backgroundColor: botBgColor,
                        borderRadius: `${borderRadius}px`,
                        borderTopLeftRadius: '0',
                        borderBottomRightRadius: `${borderRadius}px`,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    }}
                >
                    {/* Content */}
                    <div className="zentri-flex zentri-flex-col zentri-gap-3">
                        {message.blocks.map((block, index) => {
                            if (block.type === 'text') {
                                return <TextBlockComponent key={index} block={block} delay={index * 0.1} textColor={botTextColor} fontSize={fontSize} primaryColor={customStyles?.primaryColor} />;
                            } else if (block.type === 'image') {
                                return <ImageBlockComponent key={index} block={block} delay={index * 0.1} />;
                            }
                            return null;
                        })}
                    </div>
                </div>

                {/* Undo button - appears on hover */}
                {onUndo && (
                    <button
                        onClick={onUndo}
                        className="zentri-opacity-0 zentri-group-hover:zentri-opacity-100 zentri-transition-all zentri-duration-300 zentri-p-1.5 zentri-rounded-md hover:zentri-bg-neutral-100 zentri-text-neutral-500 hover:zentri-text-neutral-700 zentri-flex-shrink-0 zentri-mt-1 hover:zentri-scale-110"
                        title="Undo message"
                        aria-label="Undo message"
                    >
                        <Undo2 size={14} strokeWidth={2} />
                    </button>
                )}
            </div>
        </div>
    );
};

const TextBlockComponent: React.FC<{ block: TextBlock; delay?: number; textColor?: string; fontSize?: number; primaryColor?: string }> = ({ block, delay = 0, textColor = '#1f2937', fontSize = 14, primaryColor }) => {
    return (
        <div
            className="zentri-leading-relaxed zentri-animate-fade-slide-up"
            style={{
                animationDelay: `${delay}s`,
                color: textColor,
                fontSize: `${fontSize}px`,
                ...(primaryColor ? { ['--zentri-primary' as string]: primaryColor } : {}),
            }}
            dangerouslySetInnerHTML={parseFormattedText(block.content)}
        />
    );
};

const ImageBlockComponent: React.FC<{ block: ImageBlock; delay?: number }> = ({ block, delay = 0 }) => {
    const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

    const handleImageLoad = (index: number) => {
        setLoadedImages((prev) => new Set(prev).add(index));
    };

    const scale = block.scale || 1;

    // Carousel mode
    if (block.carousel && block.images.length >= 2) {
        return <ImageCarousel block={block} delay={delay} />;
    }

    // Grid mode (default)
    return (
        <div
            className={cn(
                getGridClass(block.showPerRow),
                'zentri-animate-fade-slide-up'
            )}
            style={{ animationDelay: `${delay}s` }}
        >
            {block.images.map((image, index) => (
                <div
                    key={index}
                    className="zentri-relative zentri-rounded-lg zentri-overflow-hidden zentri-bg-neutral-100 zentri-transition-all zentri-duration-300 zentri-ease-out hover:zentri-shadow-elegant"
                    style={{
                        maxHeight: '300px',
                        transform: scale !== 1 ? `scale(${scale})` : undefined,
                        transformOrigin: 'center',
                    }}
                >
                    <img
                        src={image.url}
                        alt={image.alt || `Image ${index + 1}`}
                        className={cn(
                            'zentri-w-full zentri-h-auto zentri-object-cover zentri-rounded-lg',
                            'zentri-transition-all zentri-duration-500',
                            loadedImages.has(index) ? 'zentri-opacity-100' : 'zentri-opacity-0'
                        )}
                        style={{ maxHeight: '300px' }}
                        onLoad={() => handleImageLoad(index)}
                        loading="lazy"
                    />
                </div>
            ))}
        </div>
    );
};

const ImageCarousel: React.FC<{ block: ImageBlock; delay?: number }> = ({ block, delay = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
    const [isHovered, setIsHovered] = useState(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const total = block.images.length;
    const autoDelay = (block.carouselDelay || 4) * 1000;

    const handleImageLoad = (index: number) => {
        setLoadedImages((prev) => new Set(prev).add(index));
    };

    const goTo = useCallback((index: number) => {
        setCurrentIndex(((index % total) + total) % total);
    }, [total]);

    const goPrev = useCallback(() => goTo(currentIndex - 1), [currentIndex, goTo]);
    const goNext = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);

    // Auto-scroll loop
    useEffect(() => {
        if (isHovered) return;
        timerRef.current = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % total);
        }, autoDelay);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [autoDelay, total, isHovered]);

    return (
        <div
            className="zentri-relative zentri-w-full zentri-animate-fade-slide-up zentri-select-none"
            style={{ animationDelay: `${delay}s` }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Image viewport */}
            <div className="zentri-relative zentri-overflow-hidden zentri-rounded-lg zentri-bg-neutral-100" style={{ maxHeight: '300px' }}>
                <div
                    className="zentri-flex zentri-transition-transform zentri-duration-500 zentri-ease-in-out"
                    style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                    {block.images.map((image, index) => (
                        <div
                            key={index}
                            className="zentri-w-full zentri-flex-shrink-0"
                            style={{ maxHeight: '300px' }}
                        >
                            <img
                                src={image.url}
                                alt={image.alt || `Image ${index + 1}`}
                                className={cn(
                                    'zentri-w-full zentri-h-auto zentri-object-cover',
                                    'zentri-transition-opacity zentri-duration-500',
                                    loadedImages.has(index) ? 'zentri-opacity-100' : 'zentri-opacity-0'
                                )}
                                style={{ maxHeight: '300px' }}
                                onLoad={() => handleImageLoad(index)}
                                loading={index <= 1 ? 'eager' : 'lazy'}
                            />
                        </div>
                    ))}
                </div>

                {/* Left chevron */}
                <button
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    className="zentri-absolute zentri-left-1.5 zentri-top-1/2 -zentri-translate-y-1/2 zentri-w-7 zentri-h-7 zentri-rounded-full zentri-bg-white/80 zentri-flex zentri-items-center zentri-justify-center zentri-text-neutral-700 zentri-transition-opacity zentri-duration-200 zentri-opacity-0 hover:zentri-bg-white zentri-cursor-pointer zentri-backdrop-blur-sm"
                    style={{ opacity: isHovered ? 0.9 : 0 }}
                    aria-label="Previous image"
                >
                    <ChevronLeft size={16} strokeWidth={2.5} />
                </button>

                {/* Right chevron */}
                <button
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    className="zentri-absolute zentri-right-1.5 zentri-top-1/2 -zentri-translate-y-1/2 zentri-w-7 zentri-h-7 zentri-rounded-full zentri-bg-white/80 zentri-flex zentri-items-center zentri-justify-center zentri-text-neutral-700 zentri-transition-opacity zentri-duration-200 zentri-opacity-0 hover:zentri-bg-white zentri-cursor-pointer zentri-backdrop-blur-sm"
                    style={{ opacity: isHovered ? 0.9 : 0 }}
                    aria-label="Next image"
                >
                    <ChevronRight size={16} strokeWidth={2.5} />
                </button>
            </div>

            {/* Dots indicator */}
            {total > 1 && (
                <div className="zentri-flex zentri-justify-center zentri-gap-1.5 zentri-mt-2">
                    {block.images.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goTo(index)}
                            className={cn(
                                'zentri-w-1.5 zentri-h-1.5 zentri-rounded-full zentri-transition-all zentri-duration-300 zentri-cursor-pointer',
                                index === currentIndex
                                    ? 'zentri-bg-neutral-600 zentri-w-3'
                                    : 'zentri-bg-neutral-300 hover:zentri-bg-neutral-400'
                            )}
                            aria-label={`Go to image ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
